import { Table, Input, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageOutlined } from "@ant-design/icons";

import "./OneOnOneBoard.scss";
import { OneOnOneMessageListDataType, FiltersType } from "./types";
import CustomFilterDropdown from "./utils/CustomFilterDropdown";

import { header } from "../utils/salesAdminHeader";

import ContentTitle from "../../../components/ui/ContentTitle";
import { apiCall } from "../../../utils/apiCall";

//Permission usage
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { __currentUser__ } from "../../../graphql/policies";
import { ServiceTypes } from "../../../types/types";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { renderDecodedId } from "../../../utils/renderDecodedId";

const { Search } = Input;

interface OneOnOneBoardProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const OneOnOneBoard = ({
  setSelectedService,
  setSelectSubItem,
}: OneOnOneBoardProps) => {
  const [messages, setMessages] = useState<OneOnOneMessageListDataType[]>([]);
  const [searchedMessages, setSearchedMessages] = useState<
    OneOnOneMessageListDataType[]
  >([]);
  const [tableHeight, setTableHeight] = useState(425);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState<FiltersType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 20;
  const navigate = useNavigate();

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const fetchData = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint: process.env.REACT_APP_SALES_ADMIN + "/contents/admin/messages",
    });
    setMessages(data);
    setIsLoading(false);
  };

  const onSearch = (value: string) => {
    if (value) {
      setIsSearching(true);
      setSearchedMessages(
        messages.filter(
          (message) =>
            message.title.includes(value) || message.creator.includes(value)
        )
      );
    } else {
      setIsSearching(false);
    }
  };

  const columns: ColumnsType<OneOnOneMessageListDataType> = [
    {
      title: "번호",
      render: (_, __, index) =>
        isSearching
          ? searchedMessages.length - (currentPage - 1) * pageSize - index
          : messages.length - (currentPage - 1) * pageSize - index,
      width: "5%",
    },
    {
      title: "분류",
      key: "category",
      dataIndex: "category",
      width: "8%",
      filters: categoryFilters,
      filterDropdown: (props) => (
        <CustomFilterDropdown
          {...props}
          okButtonText="확인"
          resetButtonText="초기화"
        />
      ),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: "제목",
      width: "25%",
      render: (_, record) => {
        return (
          <div className="flex justify-between items-center">
            <p className="w-5/6">{record.title}</p>
            {record.replyCount > 0 ? (
              <div className="flex justify-end items-center">
                <MessageOutlined />
                <p className="ml-2">{record.replyCount}</p>
              </div>
            ) : (
              <></>
            )}
          </div>
        );
      },
    },
    {
      title: "작성자",
      key: "creator",
      dataIndex: "creator",
      render: (value, record) => `${value}(${record.companyNumber})`,
      width: "8%",
    },
    {
      title: "작성일",
      key: "date",
      dataIndex: "date",
      render: (v) => v.split(".")[0].replace("T", " "),
      width: "10%",
    },
    {
      title: "상태",
      render: (_, record) => {
        return (
          <div className="flex justify-center items-center">
            <div
              className={`dot ${record.isResolved ? "resolved" : "pending"}`}
            />
            <p
              className={`${
                record.isResolved ? "resolved-text" : "pending-text"
              }`}
            >
              {record.isResolved ? "답변완료" : "답변대기"}
            </p>
          </div>
        );
      },
      sorter: (a, b) => Number(a.isResolved) - Number(b.isResolved),
      width: "5%",
    },
  ];

  useEffect(() => {
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
    fetchData();
    const updateHeight = () => {
      if (window.innerHeight > 900) {
        setTableHeight(525);
      } else {
        setTableHeight(425);
      }
    };

    updateHeight();
  }, []);

  useEffect(() => {
    const uniqueCategories = Array.from(
      new Set(messages.map((item) => item.category))
    );
    setCategoryFilters(
      uniqueCategories.map((i) => {
        return { text: i, value: i };
      })
    );
  }, [messages]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="oneOnOneBoardWrapper">
      <ContentTitle title="1:1 게시판" />
      <div className="content-container">
        <div className="flex justify-end mb-5">
          <Search
            placeholder="제목이나 게시자 검색"
            allowClear
            onSearch={onSearch}
            className="w-1/3"
          />
        </div>
        <Table
          columns={columns}
          dataSource={isSearching ? searchedMessages : messages}
          pagination={{
            pageSize: 20,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          scroll={{ x: "max-content", y: tableHeight }}
          loading={isLoading}
          onRow={(record) => {
            return {
              onClick: () => {
                navigate(`/sales188/one-on-one-detail/${record.contentId}`);
              },
              style: { cursor: "pointer" },
            };
          }}
        />
      </div>
    </div>
  );
};

export default OneOnOneBoard;
